import React, { useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import GoogleLogin from 'react-google-login';
import TwitterLogin from 'react-twitter-login';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import isEmail from 'validator/lib/isEmail';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import isStrongPassword from 'validator/lib/isStrongPassword';

import { useStore, useLocalStore } from '../../../../stores';

import styles from './SignPage.module.scss';

type Props = {
    page: 'login' | 'signup',
};

type LocalStore = {
    isProcess: boolean,

    mode: '' | 'confirm',

    code: string,
    codeMessage: string,
    codeError: boolean,
    isCodeValid: boolean,

    email: string,
    emailMessage: string,
    emailError: boolean,
    isEmailValid: boolean,

    password: string,
    passwordMessage: string,
    passwordError: boolean,
    passwordShow: boolean,
    isPasswordValid: boolean,

    accept: boolean,
    acceptError: boolean,
};

export default observer(function SignPage({ page }: Props) {
    const history = useHistory();

    const { authStore } = useStore();
    const { localStore } = useLocalStore<LocalStore>({
        isProcess: false,

        mode: '',

        code: '',
        codeMessage: '',
        codeError: false,
        get isCodeValid() {
            return this.code.length === 4;
        },

        email: '',
        emailMessage: '',
        emailError: false,
        get isEmailValid() {
            return isEmail(this.email);
        },

        password: '',
        passwordMessage: '',
        passwordError: false,
        passwordShow: false,
        get isPasswordValid() {
            return !!isStrongPassword(this.password, {
                minLength: 8,
                minLowercase: 1,
                minUppercase: 1,
                minNumbers: 0,
                minSymbols: 0,
                returnScore: false,
            });
        },

        accept: false,
        acceptError: false,
    });

    useEffect(() => {
        localStore.mode = '';

        localStore.code = '';
        localStore.codeMessage = '';

        localStore.email = '';
        localStore.emailMessage = '';

        localStore.password = '';
        localStore.passwordMessage = '';

        localStore.accept = false;
        localStore.acceptError = false;
    }, [page]);

    const handleSendCode = async () => {
        if (localStore.isProcess) {
            return;
        }

        try {
            localStore.isProcess = true;
            localStore.code = '';
            localStore.codeMessage = '';

            await authStore.confirm(localStore.email);
        } catch (err) {
            localStore.codeMessage = 'Code not found. Please, try send code again.';
        } finally {
            localStore.isProcess = false;
        }
    };

    const handleFacebook = async (response: any) => {
        if (localStore.isProcess) {
            return;
        }

        if (response.status) {
            console.error(response.status || 'Undefined error');
            return;
        }
        if (!response.accessToken) {
            console.error('Undefined token');
            return;
        }

        await authStore.loginFacebook(response.id, response.accessToken);

        // history.replace(`/players/${authStore.id}`);
        history.replace('/');
    };

    const handleGoogle = async (response: any) => {
        if (localStore.isProcess) {
            return;
        }

        if (response.error) {
            console.error(response.error || 'Undefined error');
            return;
        }
        if (!response.accessToken) {
            console.error('Undefined token');
            return;
        }

        await authStore.loginGoogle(response.googleId, response.accessToken);

        // history.replace(`/players/${authStore.id}`);
        history.replace('/');
    };

    const handleSubmit = async () => {
        if (localStore.isProcess) {
            return;
        }

        if (localStore.mode === '') {
            if (!localStore.isEmailValid) {
                localStore.emailMessage = 'Invalid email. Please, try another email.';
                localStore.emailError = true;
            }
            if (!localStore.isPasswordValid) {
                localStore.passwordMessage = 'Invalid password. Please, try another password.';
                localStore.passwordError = true;
            }

            if (localStore.isEmailValid && localStore.isPasswordValid) {
                localStore.isProcess = true;
                localStore.emailMessage = '';
                localStore.passwordMessage = '';

                try {
                    if (page === 'login') {
                        await authStore.login(localStore.email, localStore.password);

                        if (authStore.isAuthenticated) {
                            history.replace('/');
                        } else if (authStore.status === 'unconfirmed') {
                            localStore.mode = 'confirm';
                        } else {
                            throw new Error('Undefined error');
                        }

                        return;
                    }

                    if (page === 'signup') {
                        if (localStore.accept) {
                            await authStore.signup(localStore.email, localStore.password);

                            localStore.mode = 'confirm';
                        } else {
                            localStore.acceptError = true;
                        }

                        return;
                    }
                } catch (err) {
                    localStore.emailMessage = 'Invalid email. Please, try another email.';
                    localStore.emailError = true;

                    localStore.passwordMessage = 'Invalid password. Please, try another password.';
                    localStore.passwordError = true;
                } finally {
                    localStore.isProcess = false;
                }
            }

            return;
        }

        if (localStore.mode === 'confirm') {
            if (localStore.isCodeValid) {
                localStore.isProcess = true;

                try {
                    await authStore.confirmVerify(localStore.email, localStore.code);
                    await authStore.login(localStore.email, localStore.password);

                    if (authStore.isAuthenticated) {
                        history.replace('/');
                    } else {
                        history.replace('/auth/login');
                    }

                    localStore.mode = '';
                    localStore.emailMessage = '';
                    localStore.passwordMessage = '';
                    localStore.codeMessage = '';
                } catch (err) {
                    localStore.codeMessage = 'Code not found. Please, try send code again.';
                } finally {
                    localStore.isProcess = false;
                }
            }

            return;
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.top}>
                <div className={styles.header}>
                    <div
                        className={classNames(styles.headerItem, {
                            [styles.headerItemActive]: page === 'signup',
                        })}
                        onClick={() => {
                            if (localStore.isProcess || localStore.mode === 'confirm') {
                                return;
                            }

                            history.replace('/auth/signup');
                        }}
                    >
                        Sign Up
                    </div>
                    <div
                        className={classNames(styles.headerItem, {
                            [styles.headerItemActive]: page === 'login',
                        })}
                        onClick={() => {
                            if (localStore.isProcess || localStore.mode === 'confirm') {
                                return;
                            }

                            history.replace('/auth/login');
                        }}
                    >
                        Log In
                    </div>

                    <div
                        style={{
                            position: 'absolute',
                            right: 0,
                            width: 15,
                            height: 15,
                            color: '#ffffff',
                            background: `url(${require('../../assets/icons/close.png').default}) no-repeat center / contain`,
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            if (localStore.isProcess) {
                                history.goBack();
                            }
                        }}
                    />
                </div>

                <div className={styles.form}>
                    {localStore.mode === '' && (
                        <>
                            <div
                                className={classNames(styles.formItem, {
                                    [styles.formItemError]: localStore.emailError && localStore.emailMessage,
                                })}
                            >
                                <div className={styles.formItemGroup}>
                                    <div
                                        style={{
                                            width: 15,
                                            height: 15,
                                            marginRight: 20,
                                            background: `url(${require('../../assets/icons/profile.png').default}) no-repeat center / contain`,
                                        }}
                                    />

                                    <input
                                        inputMode="email"
                                        type="email"
                                        name="email"
                                        placeholder="E-Mail"
                                        value={localStore.email}
                                        onChange={(event) => {
                                            localStore.email = event.target.value;
                                            localStore.emailMessage = '';
                                        }}
                                    />
                                </div>
                                <div className={styles.formItemMessage}>
                                    {localStore.emailMessage}
                                </div>
                            </div>

                            <div
                                className={classNames(styles.formItem, {
                                    [styles.formItemError]: localStore.passwordError && localStore.passwordMessage,
                                    [styles.formItemValid]: localStore.isPasswordValid,
                                })}
                            >
                                <div className={styles.formItemGroup}>
                                    <div
                                        style={{
                                            width: 15,
                                            height: 15,
                                            marginRight: 20,
                                            background: `url(${require('../../assets/icons/lock.png').default}) no-repeat center / contain`,
                                        }}
                                    />

                                    <input
                                        inputMode="text"
                                        type={localStore.passwordShow ? 'text' : 'password'}
                                        name="password"
                                        placeholder="Password"
                                        value={localStore.password}
                                        onFocus={() => {
                                            localStore.passwordMessage = 'Min 8 characters with 1 upper and 1 lower case.';
                                            localStore.passwordError = false;
                                        }}
                                        onBlur={() => {
                                            localStore.passwordMessage = '';
                                            localStore.passwordError = false;
                                        }}
                                        onChange={(event) => {
                                            localStore.password = event.target.value;
                                        }}
                                    />

                                    <div
                                        style={{
                                            width: 23,
                                            height: 16,
                                            marginLeft: 10,
                                            background: `url(${localStore.passwordShow ? require('../../assets/icons/eye.png').default : require('../../assets/icons/eye-off.png').default}) no-repeat center / contain`,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => (localStore.passwordShow = !localStore.passwordShow)}
                                    />
                                </div>
                                <div className={styles.formItemMessage}>
                                    {localStore.passwordMessage}
                                </div>
                            </div>
                        </>
                    )}

                    {localStore.mode === 'confirm' && (
                        <>
                            <div className={styles.formItem}>
                                <div className={styles.formItemText}>
                                    Enter code which was just sent to your email {localStore.email}
                                </div>
                            </div>

                            <div
                                className={classNames(styles.formItem, {
                                    [styles.formItemError]: localStore.codeError && localStore.codeMessage,
                                    [styles.formItemValid]: localStore.isCodeValid,
                                })}
                            >
                                <div className={styles.formItemGroup}>
                                    <div
                                        style={{
                                            width: 15,
                                            height: 15,
                                            marginRight: 20,
                                            background: `url(${require('../../assets/icons/code.png').default}) no-repeat center / contain`,
                                        }}
                                    />

                                    <input
                                        inputMode="numeric"
                                        type="text"
                                        name="code"
                                        pattern="[0-9]*"
                                        placeholder="Code"
                                        value={localStore.code}
                                        onFocus={() => {
                                            localStore.codeMessage = '4 digits.';
                                            localStore.codeError = false;
                                        }}
                                        onBlur={() => {
                                            localStore.codeMessage = '';
                                            localStore.codeError = false;
                                        }}
                                        onChange={(event) => {
                                            localStore.code = event.target.value.replace(/\D+/g, '').slice(0, 4);
                                        }}
                                    />
                                </div>
                                <div className={styles.formItemMessage}>
                                    {localStore.codeMessage}
                                </div>
                            </div>
                        </>
                    )}

                    <div className={styles.formExtra}>
                        {localStore.mode === '' && (
                            <>
                                {page === 'login' && (
                                    <div className={styles.formExtraItem}>
                                        <span onClick={() => history.replace('/auth/recovery')}>
                                            Forgot Password?
                                        </span>
                                    </div>
                                )}

                                {page === 'signup' && (
                                    <div
                                        className={classNames(styles.formExtraItemAccept, {
                                            [styles.formExtraItemAcceptActive]: localStore.accept,
                                            [styles.formExtraItemAcceptError]: localStore.acceptError,
                                        })}
                                    >
                                        <span
                                            onClick={() => {
                                                localStore.accept = !localStore.accept;
                                                localStore.acceptError = false;
                                            }}
                                        >
                                            I agree with <Link to="/terms" target="_blank">Terms & Conditions</Link>
                                        </span>
                                    </div>
                                )}
                            </>
                        )}

                        {localStore.mode === 'confirm' && (
                            <div className={styles.formExtraItem}>
                                <span onClick={handleSendCode}>
                                    Send another code
                                </span>
                            </div>
                        )}
                    </div>

                    <div className={styles.formSubmit}>
                        <div className={styles.formSubmitButton} onClick={handleSubmit}>
                            {localStore.mode === '' ? (page === 'login' ? 'Log In' : 'Sign Up') : 'Verify email & Continue'}
                        </div>
                    </div>
                </div>

                <div className={styles.social}>
                    <FacebookLogin
                        isMobile={false}
                        appId={process.env.REACT_APP_FACEBOOK_APP_ID || ''}
                        render={(props: any) => (
                            <div
                                className={styles.facebook}
                                onClick={props.onClick}
                            />
                        )}
                        callback={handleFacebook}
                    />

                    <GoogleLogin
                        clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}
                        cookiePolicy="single_host_origin"
                        buttonText="Login"
                        render={(props: any) => (
                            <div
                                className={styles.google}
                                onClick={props.onClick}
                            />
                        )}
                        onSuccess={handleGoogle}
                        onFailure={handleGoogle}
                    />

                    <TwitterLogin
                        className={styles.twitter}
                        consumerKey=""
                        consumerSecret=""
                        authCallback={() => {
                        }}
                    >
                        &nbsp;
                    </TwitterLogin>
                </div>
            </div>

            <div className={styles.bottom}>
                <div>
                    <p style={{ textAlign: 'center' }}>
                        Create always FREE account and you’ll get: <br/>
                        <span style={{ fontWeight: 100 }}>
                            - Simplest location based search of
                        </span>
                    </p>

                    <p style={{ textAlign: 'center' }}>
                        PLAYERS & COACHES all around the world! <br/>
                        <span style={{ fontWeight: 100 }}>
                            - Invitations to play from best players! <br/>
                            - Receive promotions from coaches and courts! <br/>
                            - Amateur ELLO rating! <br/>
                            - Access to ALL WORLD TENNIS local communities with ability to rise in local ratings!
                        </span>
                    </p>

                    <p style={{ textAlign: 'center' }}>
                        A premium account for only $5 / month additionally allow you: <br/>
                        <span style={{ fontWeight: 100 }}>
                            - See daily schedule of all users in the world! <br/>
                            - Invite anyone to play and book trainings on-line! <br/>
                            - Add unlimited favourites lists of players and courts. <br/>
                            - Host and participate in tournaments!
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
});
